import React, { useState } from "react";

import {
  Tooltip,
  styled,
  Checkbox,
  FormControlLabel,
  IconButton,
  Box,
} from "@mui/material";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

import ForecastToggle from "../ForecastToggle";
import ProjectionHelpDialog from "./ProjectionHelpDialog";

const SCENARIO_ORDER = ["RCP8.5", "RCP2.6"];

const ControlsPanel = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "disabled" && prop !== "mobile" && prop !== "compact",
})(({ disabled, mobile, compact }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: compact ? "0px" : "6px",
  paddingLeft: mobile ? "3px" : "8px",
  paddingTop: mobile ? "6px" : 0,
  paddingBottom: mobile ? "3px" : 0,
  flexShrink: 0,
  height: compact ? "32px" : "80px",
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? "none" : "auto",
  border: mobile ? "1px solid #949494" : undefined,
  // fontSize: compact ? "0.7em" : "0.85em",
}));

const ScenarioGroup = styled("div", {
  shouldForwardProp: (prop) => prop !== "compact",
})(({ compact }) => ({
  display: "flex",
  flexDirection: "column",
  gap: compact ? "1px" : "4px",
}));

const RadioLabel = styled("label", {
  shouldForwardProp: (prop) => prop !== "disabled" && prop !== "compact",
})(({ disabled, compact, theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontSize: compact ? "0.65em" : "0.7em",
  cursor: disabled ? "not-allowed" : "pointer",
  // opacity: disabled ? 0.5 : 1,
  padding: "0",
  whiteSpace: "nowrap",
  "& input[type='radio']": {
    width: compact ? "10px" : "12px",
    height: compact ? "10px" : "12px",
    margin: 0,
    cursor: disabled ? "not-allowed" : "pointer",
    accentColor: theme.palette.brand?.blue,
  },
}));

/**
 * Projection controls panel (shared desktop/mobile).
 * Renders scenario radios, ForecastToggle, and change-mode checkbox.
 */
function ProjectionControls({
  disabled,
  mobile,
  szenarioItems,
  szenarioIndex,
  onScenarioChange,
  isChangeMode,
  onChangeMode,
  CommunicationStore,
  token,
  view,
  map,
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  // On mobile, collapse to a compact row when disabled so it doesn't take up
  // unnecessary space. On desktop keep full size to avoid layout shift.
  const compact = !!mobile && !!disabled;

  const orderedSzenarioItems =
    szenarioItems && szenarioItems.length > 0
      ? [...szenarioItems]
          .map((item, idx) => ({ ...item, originalIndex: idx }))
          .sort((a, b) => {
            const ai = SCENARIO_ORDER.indexOf(a.label);
            const bi = SCENARIO_ORDER.indexOf(b.label);
            return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
          })
      : [];

  return (
    <>
      <ProjectionHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
      <Tooltip
        title="Auswahl nur für Projektionsdaten ist relevant"
        placement={mobile ? "bottom" : "top"}
        arrow
        disableHoverListener={!disabled}
        disableFocusListener={!disabled}
        disableTouchListener={!disabled}
      >
        <span style={{ cursor: disabled ? "not-allowed" : "default" }}>
          <ControlsPanel
            compact={compact}
            id="klimaSliderFutureControls"
            disabled={disabled}
            aria-disabled={disabled}
            mobile={mobile}
          >
            <ScenarioGroup compact={compact}>
              {orderedSzenarioItems.length > 0 ? (
                orderedSzenarioItems.map((item) => (
                  <RadioLabel
                    key={item.originalIndex}
                    disabled={disabled}
                    compact={compact}
                  >
                    <input
                      type="radio"
                      name="scenario"
                      checked={szenarioIndex === item.originalIndex}
                      onChange={() => onScenarioChange(item.originalIndex)}
                    />
                    {item?.label}
                  </RadioLabel>
                ))
              ) : (
                <>
                  {SCENARIO_ORDER.map((label) => (
                    <RadioLabel key={label} disabled={true} compact={compact}>
                      <input type="radio" name="scenario" disabled />
                      {label}
                    </RadioLabel>
                  ))}
                </>
              )}
            </ScenarioGroup>

            <ForecastToggle
              CommunicationStore={CommunicationStore}
              attributeId="Characteristic"
              token={token}
              view={view}
              map={map}
              compact={compact}
            />

            <FormControlLabel
              labelPlacement="end"
              control={
                <Checkbox
                  checked={isChangeMode}
                  onChange={(e) => onChangeMode(e.target.checked)}
                  size="small"
                  sx={{ padding: compact ? 0 : "2px" }}
                />
              }
              label="Änderung zu 1971-2000"
              sx={{
                margin: 0,
                gap: "4px",
                alignItems: "center",
                "& .MuiFormControlLabel-label": {
                  fontSize: compact ? "0.6em" : "0.65em",
                  minInlineSize: "51px",
                },
                // opacity: disabled ? 0.5 : 1,
              }}
            />
          </ControlsPanel>
        </span>
      </Tooltip>

      <Tooltip title="Hilfe zu Zukunftszeiträumen">
        <IconButton
          onClick={() => setHelpOpen(true)}
          size="small"
          sx={{
            padding: "2px",
            alignSelf: "flex-start",
            ...(mobile && {
              position: "absolute",
              right: 1,
              top: 36,
            }),
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              border: "2px solid #d3d3d3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
            }}
          >
            <QuestionMarkIcon
              sx={(theme) => ({
                fontSize: 12,
                color: theme.palette.brand?.red,
              })}
            />
          </Box>
        </IconButton>
      </Tooltip>
    </>
  );
}

export default ProjectionControls;
